import React, { useState, useEffect, useRef } from "react";


export function BusyBee({ onRetry }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, background: "#FFFBEB", border: "1px solid #FCD34D", borderRadius: 10, padding: "12px 18px", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 14, color: "#92400E", margin: "12px 0" }}>
      <span style={{ fontSize: 20 }}>🐝</span>
      <span style={{ flex: 1 }}>Our bees are a little busy right now — hang tight and try again in a moment.</span>
      {onRetry && (
        <button onClick={onRetry} style={{ background: "#F59E0B", color: "#fff", border: "none", borderRadius: 6, padding: "6px 14px", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 13, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap" }}>
          Try Again
        </button>
      )}
    </div>
  );
}

// ── Bee-themed metric card ─────────────────────────────────────────────────
export function MetricCard({ label, value, sub, accent }) {
  const A = accent || "#0077B5";
  return (
    <div style={{ background: "#fff", border: "1px solid #E1E9EE", borderRadius: 12, padding: "20px 22px", flex: 1, minWidth: 0 }}>
      <div style={{ fontSize: 11, fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", color: "#999", letterSpacing: "0.12em", marginBottom: 8, textTransform: "uppercase" }}>{label}</div>
      <div style={{ fontSize: 28, fontWeight: 700, color: A, fontFamily: "'Playfair Display',Georgia,serif", lineHeight: 1.1 }}>{value}</div>
      {sub && <div style={{ fontSize: 12, color: "#888", marginTop: 6, fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif" }}>{sub}</div>}
    </div>
  );
}

// ── Simple SVG bar + line chart (no external deps) ────────────────────────
export function BeeChart({ data, valueKey, projKey, labelKey, title, height = 180, barColor = "#185FA5", lineColor = "#1D9E75" }) {
  if (!data || data.length === 0) return null;
  const vals = data.map(d => d[valueKey] || 0);
  const projs = projKey ? data.map(d => d[projKey] || 0) : [];
  const allVals = [...vals, ...projs];
  const maxVal = Math.max(...allVals, 1);
  const W = 600;
  const H = height;
  const PAD = { top: 16, right: 16, bottom: 36, left: 48 };
  const chartW = W - PAD.left - PAD.right;
  const chartH = H - PAD.top - PAD.bottom;
  const barW = Math.floor(chartW / data.length * 0.5);
  const gap = chartW / data.length;

  const yScale = v => PAD.top + chartH - (v / maxVal) * chartH;
  const xCenter = i => PAD.left + i * gap + gap / 2;

  // Y axis ticks
  const ticks = [0, Math.round(maxVal / 2), maxVal];

  return (
    <div style={{ width: "100%", overflowX: "auto" }}>
      {title && <div style={{ fontSize: 11, color: "#999", letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: 8, fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif" }}>{title}</div>}
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", maxWidth: W, display: "block" }} aria-label={title || "chart"}>
        {/* Y grid lines */}
        {ticks.map((t, i) => (
          <g key={i}>
            <line x1={PAD.left} y1={yScale(t)} x2={W - PAD.right} y2={yScale(t)} stroke="#F0F0F0" strokeWidth={1} />
            <text x={PAD.left - 6} y={yScale(t) + 4} textAnchor="end" fontSize={10} fill="#AAAAAA">{t >= 1000 ? `${Math.round(t/1000)}k` : t}</text>
          </g>
        ))}
        {/* Bars */}
        {data.map((d, i) => {
          const v = d[valueKey] || 0;
          const bh = (v / maxVal) * chartH;
          const x = xCenter(i) - barW / 2;
          const y = yScale(v);
          return (
            <rect key={i} x={x} y={y} width={barW} height={bh} rx={3} fill={barColor} opacity={0.8} />
          );
        })}
        {/* Actual line (if values exist) */}
        {vals.some(v => v > 0) && (
          <polyline
            points={data.map((_, i) => `${xCenter(i)},${yScale(vals[i])}`).join(" ")}
            fill="none" stroke={barColor} strokeWidth={2} opacity={0.9}
          />
        )}
        {/* Projected dashed line */}
        {projKey && projs.some(v => v > 0) && (
          <polyline
            points={data.map((_, i) => `${xCenter(i)},${yScale(projs[i])}`).join(" ")}
            fill="none" stroke={lineColor} strokeWidth={2} strokeDasharray="6,4" opacity={0.85}
          />
        )}
        {/* X labels */}
        {data.map((d, i) => (
          <text key={i} x={xCenter(i)} y={H - 6} textAnchor="middle" fontSize={10} fill="#AAAAAA">{d[labelKey]}</text>
        ))}
      </svg>
      {/* Legend */}
      <div style={{ display: "flex", gap: 20, marginTop: 4 }}>
        {vals.some(v => v > 0) && (
          <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: "#888", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif" }}>
            <span style={{ display: "inline-block", width: 18, height: 3, background: barColor, borderRadius: 2 }} />
            Actual
          </div>
        )}
        {projKey && projs.some(v => v > 0) && (
          <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: "#888", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif" }}>
            <span style={{ display: "inline-block", width: 18, height: 3, background: lineColor, borderRadius: 2, borderTop: `2px dashed ${lineColor}` }} />
            Projected
          </div>
        )}
      </div>
    </div>
  );
}


// ── Activity Dashboard (after first week) ────────────────────────────────
export function ActivityDashboard({ ds, user, dashView, setDashView, API_BASE, onReconnect, onUpgrade, onSetupPlug, onGeneratePosts, generating }) {
  const G = "#0077B5";
  const GRN = "#1D9E75";
  const CARD = "#FFFFFF";
  const B = "#E1E9EE";
  const firstName = (ds.linkedin_name || user?.linkedin_name || ds.first_name || user?.first_name || "there").split(" ")[0];
  const plan = ds.plan || "free";
  const avg = ds.avg_impressions_per_post;

  const totalImpressions = Math.round(ds.total_posts_sent * avg);
  const weekImpressions = Math.round(ds.posts_this_week * avg);

  // Merge weekly chart data with projected
  const weekChartData = (ds.weekly_data || []).map((d) => ({
    label: d.date.split(" ").slice(0, 2).join(" "),
    actual: d.est_impressions,
    projected: Math.round(ds.posts_per_week / 7 * avg),
  }));

  const monthChartData = (ds.monthly_data || []).map((d, i) => {
    const proj = ds.projected_monthly[i];
    return {
      label: d.month,
      actual: d.est_impressions,
      projected: proj ? proj.est_impressions : 0,
    };
  });

  // Upsell
  const nextTier = plan === "free" ? { name: "Presence", extra: (16 - 12) * avg } : plan === "presence" ? { name: "Authority", extra: (20 - 16) * avg } : null;

  const hour = new Date().getHours();
  const greeting = hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";

  const recentPosts = ds.recent_posts || [];
  const upcomingPosts = ds.upcoming_posts || [];

  return (
    <div className="fade-up">

      {/* ── Plug setup reminder — solo Authority+ users only (no workspace) ── */}
      {["authority","team","agency","authority_team","pro_team"].includes(plan) && !ds.has_plug_configured && !user?.is_workspace_member && !user?.is_workspace_admin && onSetupPlug && (
        <div style={{ background: "#FFFBEB", border: "1px solid #FCD34D", borderRadius: 10, padding: "14px 20px", marginBottom: 20, display: "flex", alignItems: "center", gap: 14, fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif" }}>
          <span style={{ fontSize: 20 }}>🍯</span>
          <div style={{ flex: 1 }}>
            <span style={{ fontSize: 14, color: "#92400E", fontWeight: 600 }}>Set up your Authority Presence</span>
            <span style={{ fontSize: 13, color: "#92400E", marginLeft: 8 }}>— stay visible even when life gets busy. Set it up once, it runs forever.</span>
          </div>
          <button onClick={onSetupPlug} style={{ background: "#D97706", color: "#fff", border: "none", borderRadius: 6, padding: "7px 16px", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 13, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap" }}>Set it up →</button>
        </div>
      )}

      {/* ── Plug upsell — Presence users ── */}
      {plan === "presence" && !user?.is_workspace_member && onUpgrade && (
        <div style={{ background: "#EBF5FB", border: "1px solid #B3D4E8", borderRadius: 10, padding: "14px 20px", marginBottom: 20, display: "flex", alignItems: "center", gap: 14, fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif" }}>
          <span style={{ fontSize: 20 }}>&#127919;</span>
          <div style={{ flex: 1 }}>
            <span style={{ fontSize: 14, color: "#005885", fontWeight: 600 }}>Unlock weekly plug posts</span>
            <span style={{ fontSize: 13, color: "#005885", marginLeft: 8 }}>— one post a week, purely about you and what you offer.</span>
          </div>
          <button onClick={onUpgrade} style={{ background: "#0077B5", color: "#fff", border: "none", borderRadius: 6, padding: "7px 16px", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 13, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap" }}>Upgrade to Authority →</button>
        </div>
      )}

      {/* ── A. Hero row ── */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 24, alignItems: "start", marginBottom: 32 }} className="analytics-grid">
        {/* Left: greeting */}
        <div>
          <div style={{ fontSize: 11, color: "#999", letterSpacing: "0.15em", textTransform: "uppercase", marginBottom: 6, fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif" }}>
            {greeting}, {firstName}
          </div>
          <div style={{ fontFamily: "'Playfair Display',Georgia,serif", fontSize: 30, lineHeight: 1.2 }}>Your colony is <em style={{ color: GRN }}>growing.</em></div>
        </div>
        {/* Right: metric pills */}
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", justifyContent: "flex-end" }}>
          {[
            { label: "Posts this week", value: ds.posts_this_week },
            { label: "Est. reach this week", value: weekImpressions.toLocaleString() },
            { label: "Total posts", value: ds.total_posts_sent },
            { label: "All-time reach", value: totalImpressions.toLocaleString() },
          ].map(({ label, value }) => (
            <div key={label} style={{ background: CARD, border: `1px solid ${B}`, borderRadius: 20, padding: "8px 16px", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", textAlign: "center", whiteSpace: "nowrap" }}>
              <div style={{ fontSize: 18, fontWeight: 700, color: G, lineHeight: 1.1 }}>{value}</div>
              <div style={{ fontSize: 10, color: "#999", marginTop: 2, letterSpacing: "0.06em" }}>{label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── B. Recent scouts — post history ── */}
      <div style={{ background: CARD, border: `1px solid ${B}`, borderRadius: 12, padding: "24px 28px", marginBottom: 24 }}>
        <div style={{ fontSize: 11, color: "#999", letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: 16, fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif" }}>Recent scouts</div>
        {recentPosts.length === 0 ? (
          <div style={{ fontSize: 14, color: "#999", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif" }}>
            No posts published yet — your first worker bee is on its way.
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
            {recentPosts.slice(0, 10).map((post, i) => {
              const title = (post.article_title || "Untitled").length > 60
                ? (post.article_title || "Untitled").slice(0, 60) + "…"
                : (post.article_title || "Untitled");
              const estImpressions = Math.round(avg);
              const isFailed = post.status === "failed";
              const analyticsUrl = post.linkedin_post_id
                ? `https://www.linkedin.com/dashboard/`
                : null;
              return (
                <div key={post.id || i} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 0", borderBottom: i < recentPosts.slice(0, 10).length - 1 ? `1px solid ${B}` : "none" }}>
                  <div style={{ width: 8, height: 8, borderRadius: "50%", background: isFailed ? "#C0392B" : GRN, flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, color: "#1A1A1A", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{title}</div>
                  </div>
                  <div style={{ fontSize: 12, color: "#888", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", whiteSpace: "nowrap" }}>{post.posted_at}</div>
                  <div style={{ fontSize: 12, color: GRN, fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", whiteSpace: "nowrap" }}>~{estImpressions.toLocaleString()}</div>
                  {isFailed && (
                    <span style={{ fontSize: 11, background: "#FEF2F2", color: "#C0392B", border: "1px solid #FECDD3", borderRadius: 4, padding: "2px 7px", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontWeight: 600, whiteSpace: "nowrap" }}>failed</span>
                  )}
                  {analyticsUrl && (
                    <a href={analyticsUrl} target="_blank" rel="noreferrer"
                      style={{ fontSize: 12, color: G, fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", textDecoration: "none", whiteSpace: "nowrap" }}
                      onMouseEnter={e => e.target.style.textDecoration = "underline"}
                      onMouseLeave={e => e.target.style.textDecoration = "none"}
                    >View analytics →</a>
                  )}
                </div>
              );
            })}
          </div>
        )}
        <div style={{ marginTop: 16, paddingTop: 12, borderTop: recentPosts.length > 0 ? `1px solid ${B}` : "none" }}>
          <span style={{ fontSize: 13, color: G, fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", cursor: "pointer" }}>View all →</span>
        </div>
      </div>

      {/* ── C. Upcoming posts — queued worker bees ── */}
      <div style={{ background: CARD, border: `1px solid ${B}`, borderRadius: 12, padding: "24px 28px", marginBottom: 24 }}>
        <div style={{ fontSize: 11, color: "#999", letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: 16, fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif" }}>Queued worker bees</div>
        {generating ? (
          <div style={{ display: "flex", alignItems: "center", gap: 14, background: "#F0F7FF", border: "1px solid #B3D9F0", borderRadius: 10, padding: "18px 20px" }}>
            <div style={{ fontSize: 26 }}>🐝</div>
            <div>
              <div style={{ fontSize: 14, fontWeight: 600, color: "#1A1A1A", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", marginBottom: 3 }}>Your bees are writing your posts…</div>
              <div style={{ fontSize: 13, color: "#666", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif" }}>This takes about a minute. Your posts will appear here automatically.</div>
            </div>
          </div>
        ) : upcomingPosts.length === 0 ? (
          <div>
            <div style={{ fontSize: 14, color: "#999", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", marginBottom: 16 }}>
              No posts queued — your next batch auto-generates Monday.
            </div>
            {onGeneratePosts && (
              <div style={{ background: "#F0F7FF", border: `1px solid #B3D9F0`, borderRadius: 10, padding: "16px 20px", display: "flex", alignItems: "center", gap: 16 }}>
                <span style={{ fontSize: 22 }}>✨</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: "#1A1A1A", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", marginBottom: 2 }}>Feeling inspired?</div>
                  <div style={{ fontSize: 13, color: "#666", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif" }}>Generate your posts for the week right now.</div>
                </div>
                <button onClick={onGeneratePosts} style={{ background: G, color: "#fff", border: "none", borderRadius: 8, padding: "9px 18px", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 13, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap" }}>
                  Generate now →
                </button>
              </div>
            )}
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
            {upcomingPosts.slice(0, 3).map((post, i) => {
              const rawTitle = post.is_plug ? "Your weekly plug" : (post.article_title || "Untitled");
              const title = rawTitle.length > 60 ? rawTitle.slice(0, 60) + "…" : rawTitle;
              return (
                <div key={post.id || i} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 0", borderBottom: i < upcomingPosts.slice(0, 3).length - 1 ? `1px solid ${B}` : "none" }}>
                  <div style={{ width: 8, height: 8, borderRadius: "50%", background: post.is_plug ? "#D97706" : "#F59E0B", flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, color: "#1A1A1A", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{title}</div>
                  </div>
                  <div style={{ fontSize: 12, color: "#888", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", whiteSpace: "nowrap" }}>{post.scheduled_time}</div>
                  {post.is_plug
                    ? <span style={{ fontSize: 11, background: "#FEF3C7", color: "#92400E", border: "1px solid #FCD34D", borderRadius: 4, padding: "2px 7px", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontWeight: 600, whiteSpace: "nowrap" }}>plug</span>
                    : <span style={{ fontSize: 11, background: "#EBF5FB", color: G, border: `1px solid #B3D9F0`, borderRadius: 4, padding: "2px 7px", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontWeight: 600, whiteSpace: "nowrap" }}>pending</span>
                  }
                </div>
              );
            })}
            {onGeneratePosts && (
              <div style={{ marginTop: 12, paddingTop: 12, borderTop: `1px solid ${B}` }}>
                <span
                  onClick={onGeneratePosts}
                  style={{ fontSize: 12, color: "#888", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", cursor: "pointer", textDecoration: "underline" }}
                  onMouseEnter={e => e.target.style.color = G}
                  onMouseLeave={e => e.target.style.color = "#888"}
                >
                  Generate a fresh batch instead →
                </span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── D. Buzz over time chart ── */}
      <div style={{ background: CARD, border: `1px solid ${B}`, borderRadius: 12, padding: "24px 28px", marginBottom: 24 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <div style={{ fontFamily: "'Playfair Display',Georgia,serif", fontSize: 18 }}>Buzz over time</div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <div style={{ display: "flex", gap: 0, background: "#F3F2EF", borderRadius: 8, padding: 3, border: "1px solid #E1E9EE" }}>
              {[["weekly","Weekly"],["monthly","Monthly"]].map(([v, label]) => (
                <button key={v} onClick={() => setDashView(v)}
                  style={{ padding: "5px 14px", borderRadius: 6, border: "none", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 13, fontWeight: dashView === v ? 600 : 400, background: dashView === v ? "#fff" : "transparent", color: dashView === v ? G : "#888", cursor: "pointer", boxShadow: dashView === v ? "0 1px 4px rgba(0,0,0,0.08)" : "none", transition: "all 0.15s" }}
                >{label}</button>
              ))}
            </div>
            <a href="https://www.linkedin.com/dashboard/" target="_blank" rel="noreferrer"
              style={{ fontSize: 12, color: G, fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", textDecoration: "none", fontWeight: 600 }}>
              View actual analytics →
            </a>
          </div>
        </div>
        {dashView === "weekly" ? (
          <BeeChart data={weekChartData} valueKey="actual" projKey="projected" labelKey="label" title="Est. fields reached per day" barColor="#185FA5" lineColor="#1D9E75" />
        ) : (
          <BeeChart data={monthChartData} valueKey="actual" projKey="projected" labelKey="label" title="Your projected harvest" barColor="#185FA5" lineColor="#1D9E75" />
        )}
      </div>

      {/* ── E. Upsell banner ── */}
      {nextTier && !["authority","team","agency"].includes(plan) && (
        <div style={{ background: "linear-gradient(135deg, #EBF5FB 0%, #F0FAF4 100%)", border: "1px solid #B3D9F0", borderRadius: 12, padding: "18px 24px", marginBottom: 24, display: "flex", alignItems: "center", gap: 16, fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif" }}>
          <span style={{ fontSize: 24 }}>🐝</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: "#1A1A1A", marginBottom: 3 }}>Your hive has more to give</div>
            <div style={{ fontSize: 13, color: "#666" }}>Upgrade to {nextTier.name} and reach ~{Math.round(nextTier.extra).toLocaleString()} more people every month.</div>
          </div>
          {onUpgrade && <button onClick={onUpgrade} style={{ background: G, color: "#fff", border: "none", borderRadius: 8, padding: "10px 20px", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 14, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap" }}>Upgrade →</button>}
        </div>
      )}
    </div>
  );
}

// ── Compliance Checker Component ──────────────────────────────────────────
export function ComplianceChecker({ caption, plugPostId, API_BASE, onCaptionFixed, onResult }) {
  const [checking, setChecking] = useState(false);
  const [result, setResult] = useState(null);
  const [acknowledged, setAcknowledged] = useState({});
  const [fixing, setFixing] = useState(false);
  const debounceRef = useRef(null);

  const runCheck = async (text) => {
    if (!text || text.trim().length < 20) return;
    setChecking(true);
    try {
      const r = await fetch(`${API_BASE}/app/api/user/post/check`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ caption: text, plug_post_id: plugPostId }),
      });
      const d = await r.json();
      setResult(d);
      setAcknowledged({});
      if (onResult) onResult(d);
    } catch (e) {}
    setChecking(false);
  };

  // Debounced check on caption change
  useEffect(() => {
    if (!caption) return;
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runCheck(caption), 2000);
    return () => clearTimeout(debounceRef.current);
  }, [caption]); // eslint-disable-line

  const handleFixWithAI = async () => {
    if (!result?.flags) return;
    setFixing(true);
    try {
      const r = await fetch(`${API_BASE}/app/api/user/post/fix`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ caption, flags: result.flags }),
      });
      const d = await r.json();
      if (d.caption && d.changed) {
        if (onCaptionFixed) onCaptionFixed(d.caption);
        // Update result with re-checked compliance
        if (d.compliance_check) {
          setResult(d.compliance_check);
          setAcknowledged({});
          if (onResult) onResult(d.compliance_check);
        }
      } else if (d.caption) {
        // Fix didn't change text — re-check to refresh UI
        await runCheck(d.caption);
      }
    } catch (e) {}
    setFixing(false);
  };

  if (!result && !checking) return (
    <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 8 }}>
      <button onClick={() => runCheck(caption)} style={{ background: "transparent", color: "#0077B5", border: "1px solid #E1E9EE", borderRadius: 8, padding: "7px 16px", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 13, cursor: "pointer" }}>Run compliance check</button>
    </div>
  );

  if (checking) return (
    <div style={{ background: "#F9FAFB", border: "1px solid #E1E9EE", borderRadius: 10, padding: "14px 18px", marginTop: 12, fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 13, color: "#888" }}>
      Running compliance check…
    </div>
  );

  if (!result) return null;

  const blockFlags = result.flags.filter(f => f.severity === "block");
  const warnFlags = result.flags.filter(f => f.severity === "warn");
  const infoFlags = result.flags.filter(f => f.severity === "info");

  const bannerBg = result.has_blocks ? "#FEF2F2" : warnFlags.length > 0 ? "#FFFBEB" : infoFlags.length > 0 ? "#EBF5FB" : "#F0FAF4";
  const bannerBorder = result.has_blocks ? "#FECDD3" : warnFlags.length > 0 ? "#FCD34D" : infoFlags.length > 0 ? "#BFDBFE" : "#B7E4C7";
  const bannerColor = result.has_blocks ? "#B91C1C" : warnFlags.length > 0 ? "#92400E" : infoFlags.length > 0 ? "#1E40AF" : "#065F46";
  const bannerIcon = result.has_blocks ? "🚫" : warnFlags.length > 0 ? "⚠️" : infoFlags.length > 0 ? "ℹ️" : "✅";
  const bannerText = result.has_blocks
    ? "This post cannot go out yet — please fix the flagged items."
    : warnFlags.length > 0 ? "A few things to review before posting."
    : infoFlags.length > 0 ? "A couple of friendly reminders."
    : "All clear — your post passed compliance and accuracy checks.";

  return (
    <div style={{ marginTop: 12 }}>
      <div style={{ background: bannerBg, border: `1px solid ${bannerBorder}`, borderRadius: 10, padding: "12px 16px", display: "flex", alignItems: "flex-start", gap: 10 }}>
        <span style={{ fontSize: 18, flexShrink: 0 }}>{bannerIcon}</span>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: bannerColor, fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif" }}>{bannerText}</div>
          {result.flags.length > 0 && (
            <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 10 }}>
              {blockFlags.map((f, i) => (
                <div key={i} style={{ background: "#fff", border: "1px solid #FECDD3", borderRadius: 8, padding: "10px 14px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                    <span style={{ fontSize: 11, background: "#FEE2E2", color: "#B91C1C", borderRadius: 4, padding: "2px 7px", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontWeight: 700 }}>BLOCK</span>
                    <span style={{ fontSize: 13, fontWeight: 600, color: "#1A1A1A", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif" }}>{f.rule_name}</span>
                  </div>
                  <div style={{ fontSize: 12, color: "#666", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", marginBottom: 4 }}>Matched: <em>"{f.matched_text}"</em></div>
                  <div style={{ fontSize: 12, color: "#444", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif" }}>{f.suggestion}</div>
                </div>
              ))}
              {warnFlags.map((f, i) => (
                <div key={i} style={{ background: "#fff", border: "1px solid #FCD34D", borderRadius: 8, padding: "10px 14px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                    <span style={{ fontSize: 11, background: "#FEF3C7", color: "#92400E", borderRadius: 4, padding: "2px 7px", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontWeight: 700 }}>WARN</span>
                    <span style={{ fontSize: 13, fontWeight: 600, color: "#1A1A1A", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif" }}>{f.rule_name}</span>
                  </div>
                  <div style={{ fontSize: 12, color: "#666", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", marginBottom: 6 }}>Matched: <em>"{f.matched_text}"</em></div>
                  <div style={{ fontSize: 12, color: "#444", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", marginBottom: 8 }}>{f.suggestion}</div>
                  <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                    <input type="checkbox" checked={!!acknowledged[i]} onChange={e => setAcknowledged(a => ({ ...a, [i]: e.target.checked }))} />
                    <span style={{ fontSize: 12, color: "#666", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif" }}>I understand, proceed anyway</span>
                  </label>
                </div>
              ))}
              {infoFlags.map((f, i) => (
                <div key={i} style={{ background: "#fff", border: "1px solid #BFDBFE", borderRadius: 8, padding: "10px 14px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                    <span style={{ fontSize: 11, background: "#EBF5FB", color: "#1E40AF", borderRadius: 4, padding: "2px 7px", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontWeight: 700 }}>INFO</span>
                    <span style={{ fontSize: 13, fontWeight: 600, color: "#1A1A1A", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif" }}>{f.rule_name}</span>
                  </div>
                  <div style={{ fontSize: 12, color: "#444", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif" }}>{f.suggestion}</div>
                </div>
              ))}
            </div>
          )}
          {result.has_blocks && (
            <button onClick={handleFixWithAI} disabled={fixing} style={{ marginTop: 12, background: "#B91C1C", color: "#fff", border: "none", borderRadius: 8, padding: "9px 18px", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 13, fontWeight: 600, cursor: fixing ? "not-allowed" : "pointer", opacity: fixing ? 0.6 : 1 }}>
              {fixing ? "Fixing…" : "Fix with AI"}
            </button>
          )}
        </div>
        <button onClick={() => runCheck(caption)} style={{ background: "transparent", border: "none", cursor: "pointer", fontSize: 12, color: "#888", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", flexShrink: 0 }}>Re-check</button>
      </div>
    </div>
  );
}

// ── Plug Post Panel (Authority exclusive) ────────────────────────────────────
export const PLUG_TYPES = [
  { id: "shameless",      label: "Shameless Plug",   desc: "Direct, confident, unapologetic. Tell people what you do and why they should care." },
  { id: "problem_solver", label: "Problem Solver",    desc: "Lead with the pain. Show you understand it — then solve it." },
  { id: "win_post",       label: "Win Post",          desc: "A real client result. Proof that you deliver. No fluff." },
  { id: "myth_buster",    label: "Myth Buster",       desc: "Bust a misconception in your industry. Position yourself as the authority." },
  { id: "before_after",   label: "Before / After",    desc: "Paint the contrast. Where clients start, where they end up with you." },
  { id: "hot_take",       label: "Hot Take",          desc: "Your unpopular opinion. Polarizing, memorable, authentically you." },
  { id: "referral_nudge", label: "Referral Nudge",    desc: "Remind your network who you help and how to send people your way." },
  { id: "custom",         label: "Custom",             desc: "Write your own plug from scratch. Say exactly what you want, your way." },
];

export const PLUG_TONES = ["Professional","Conversational","Bold","Empathetic","Witty"];

// ── Weekly Override groupings (Phase 1A — uses existing plug_type values only) ──
export const QUICK_CAMPAIGNS = [
  { id: "win_post",  label: "Win",          desc: "A real client result. Proof that you deliver. No fluff.", emoji: "\uD83C\uDFC6" },
  { id: "custom",    label: "Custom",       desc: "Write your own override from scratch. Say exactly what you want.", emoji: "\u270F\uFE0F" },
  { id: "_announcement", label: "Announcement", desc: "Share something timely \u2014 a launch, update, or milestone.", emoji: "\uD83D\uDCE2", disabled: true },
  { id: "_offer",        label: "Offer",        desc: "Promote a limited-time deal or special offer.", emoji: "\uD83D\uDCB0", disabled: true },
  { id: "_holiday",      label: "Holiday",      desc: "Tie your message to a holiday or seasonal moment.", emoji: "\uD83C\uDF89", disabled: true },
];

export const ADVANCED_OVERRIDES = [
  { id: "shameless",      label: "Shameless",       desc: "Direct, confident, unapologetic. Tell people what you do and why they should care.", emoji: "\uD83D\uDD25" },
  { id: "problem_solver", label: "Problem Solver",   desc: "Lead with the pain. Show you understand it \u2014 then solve it.", emoji: "\uD83D\uDD27" },
  { id: "myth_buster",    label: "Myth Buster",      desc: "Bust a misconception in your industry. Position yourself as the authority.", emoji: "\uD83D\uDCA5" },
  { id: "before_after",   label: "Before / After",   desc: "Paint the contrast. Where clients start, where they end up with you.", emoji: "\uD83D\uDD04" },
  { id: "referral_nudge", label: "Referral",          desc: "Remind your network who you help and how to send people your way.", emoji: "\uD83E\uDD1D" },
  { id: "hot_take",       label: "Hot Take",          desc: "Your unpopular opinion. Polarizing, memorable, authentically you.", emoji: "\uD83C\uDF36\uFE0F" },
];

export const PLUG_QUESTIONS_MAP = {
  shameless:      ["What do you do in one sentence?","Who is your ideal client?","What makes you different from everyone else?"],
  problem_solver: ["What is the #1 problem your clients come to you with?","What does that problem cost them if ignored?","How do you solve it?"],
  win_post:       ["Describe a recent client win (no names needed)","What was the situation before you helped?","What changed after working with you?"],
  myth_buster:    ["What is the biggest myth about your industry or service?","Why do people believe it?","What is the truth?"],
  before_after:   ["What does life look like for your client BEFORE working with you?","What does life look like AFTER?","What is the one thing you do that creates that change?"],
  hot_take:       ["What is your unpopular opinion about your industry?","Why do you believe it?","What should people do differently?"],
  referral_nudge: ["Who exactly do you help? Be specific.","What is the one result you consistently deliver?","How should someone reach you or refer someone to you?"],
  custom:         ["Write your plug post below. This will go out as-is — no AI rewrite."],
};

